import Phaser from 'phaser';
import { CSS, FONTS, HEX } from '../config';
import { studentAvatarBase } from '../avatar/AvatarConfig';
import { studentAlias, fitStudentName } from './StudentIdentity';

// Modal compartido por los dos HUDs. Solo controla la interfaz: cada escena
// propietaria pausa/reanuda/reinicia/sale usando su propio flujo real.
export class PauseMenu {
  constructor(scene, { themeColor = HEX.cyan, onContinue, onRestart, onExit }) {
    this.scene = scene;
    this.themeColor = themeColor;
    this.onContinue = onContinue;
    this.onRestart = onRestart;
    this.onExit = onExit;
    this.mode = 'menu';
    this.busy = false;
    this.container = scene.add.container(0, 0).setDepth(2000);
    this.render();
  }

  get confirming() {
    return this.mode !== 'menu';
  }

  destroy() {
    if (!this.container) return;
    this.container.destroy(true);
    this.container = null;
  }

  relayout() {
    if (!this.container) return;
    this.render();
  }

  showMenu() {
    if (this.busy) return;
    this.mode = 'menu';
    this.render();
  }

  showConfirm(action) {
    if (this.busy) return;
    this.mode = action;
    this.render();
  }

  render() {
    if (!this.container) return;
    this.container.removeAll(true);

    const W = this.scene.scale.width;
    const H = this.scene.scale.height;
    const isConfirm = this.mode !== 'menu';
    const width = Math.min(W - 28, isConfirm ? 430 : 470);
    const height = Math.min(H - 28, isConfirm ? 228 : 286);
    const left = (W - width) / 2;
    const top = (H - height) / 2;

    const backdrop = this.scene.add.rectangle(W / 2, H / 2, W, H, 0x02030c, 0.72)
      .setInteractive();
    backdrop.on('pointerdown', (_pointer, _x, _y, event) => event?.stopPropagation?.());
    this.container.add(backdrop);

    const panel = this.scene.add.graphics();
    panel.fillStyle(0x071630, 0.98);
    panel.fillRoundedRect(left, top, width, height, 22);
    panel.lineStyle(3, this.themeColor, 0.96);
    panel.strokeRoundedRect(left, top, width, height, 22);
    this.container.add(panel);

    const title = this.scene.add.text(W / 2, top + 34, isConfirm
      ? (this.mode === 'restart' ? '¿REINICIAR LA MISIÓN?' : '¿SALIR DE LA MISIÓN?')
      : 'MISIÓN EN PAUSA', {
      fontFamily: FONTS.display,
      fontSize: `${Math.round(Math.min(25, Math.max(17, width * 0.06)))}px`,
      fontStyle: '900',
      color: CSS.white,
      align: 'center',
    }).setOrigin(0.5);
    this.container.add(title);
    const alias = studentAlias(this.scene);
    if (alias && !isConfirm) {
      const base = studentAvatarBase(this.scene.registry.get('studentIdentity'));
      if (base) this.container.add(this.scene.add.image(left + 2, top + 45, `portrait_${base}`)
        .setOrigin(0, 0).setCrop(0, 0, 330, 330).setScale(27 / 330).setName('student-pause-avatar'));
      const identity = this.scene.add.text(W / 2, top + 62, alias, {
        fontFamily: FONTS.friendly, fontSize: '16px', fontStyle: '800', color: '#a4fff1',
      }).setOrigin(0.5).setName('student-pause-identity');
      this.container.add(fitStudentName(identity, width - (base ? 100 : 42)));
    }

    if (isConfirm) {
      const message = this.mode === 'restart'
        ? 'El progreso de esta partida comenzará nuevamente.'
        : 'Los resultados de esta partida no se guardarán.';
      const body = this.scene.add.text(W / 2, top + 81, message, {
        fontFamily: FONTS.friendly,
        fontSize: `${Math.round(Math.min(17, Math.max(13, width * 0.04)))}px`,
        fontStyle: '800',
        color: '#dceaff',
        align: 'center',
        wordWrap: { width: width - 42, useAdvancedWrap: true },
      }).setOrigin(0.5);
      this.container.add(body);
      this.addButton(W / 2 - width * 0.22, top + height - 48, width * 0.32, 46, 'CANCELAR', 0x29436c, () => this.showMenu());
      this.addButton(
        W / 2 + width * 0.22,
        top + height - 48,
        width * 0.32,
        46,
        this.mode === 'restart' ? 'REINICIAR' : 'SALIR',
        this.mode === 'restart' ? 0x157dc1 : 0xa63d66,
        () => this.commit(),
      );
      return;
    }

    this.addButton(W / 2, top + 98, width - 62, 46, '▶  CONTINUAR', this.themeColor, () => this.commit('continue'));
    this.addButton(W / 2, top + 157, width - 62, 46, '↻  REINICIAR MISIÓN', 0x237dc6, () => this.showConfirm('restart'));
    this.addButton(W / 2, top + 216, width - 62, 46, '↩  SALIR DE LA MISIÓN', 0xa63d66, () => this.showConfirm('exit'));
  }

  addButton(x, y, width, height, label, color, handler) {
    const g = this.scene.add.graphics();
    g.fillStyle(color, 0.95);
    g.fillRoundedRect(x - width / 2, y - height / 2, width, height, 13);
    g.lineStyle(2, 0xffffff, 0.35);
    g.strokeRoundedRect(x - width / 2, y - height / 2, width, height, 13);
    g.setInteractive(
      new Phaser.Geom.Rectangle(x - width / 2, y - height / 2, width, height),
      Phaser.Geom.Rectangle.Contains,
    );
    const text = this.scene.add.text(x, y, label, {
      fontFamily: FONTS.display,
      fontSize: `${Math.round(Math.min(18, Math.max(13, width * 0.05)))}px`,
      fontStyle: '900',
      color: CSS.white,
    }).setOrigin(0.5);
    g.on('pointerdown', (_pointer, _x, _y, event) => {
      event?.stopPropagation?.();
      if (this.busy) return;
      handler();
    });
    this.container.add([g, text]);
  }

  commit(action = this.mode) {
    if (this.busy) return;
    this.busy = true;
    const callback = action === 'continue'
      ? this.onContinue
      : action === 'restart'
        ? this.onRestart
        : this.onExit;
    callback?.();
  }
}
